import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useRef, useState, useEffect } from "react";

import { Clock } from "lucide-react";
import { SmileIcon, MehIcon, FrownIcon, HelpIcon } from "@/components/icons/statusIcons";

type StatusResp = {
  status: string;
  avg?: number | null;
  distribution?: Record<string, number>;
  totalWeight?: number;
  override?: any;
};

export function VoteForm() {
  const lastUpdatedTimeRef = useRef<HTMLDivElement>(null);
  const responseInputRef = useRef<HTMLTextAreaElement>(null);
  const [status, setStatus] = useState<StatusResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [geoConsent, setGeoConsent] = useState(false);
  const [lastSentGeo, setLastSentGeo] = useState<string | null>(null);
  const [geoInfoOpen, setGeoInfoOpen] = useState(false);
  const geoInfoCloseRef = useRef<HTMLButtonElement | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch("/api/status");
      const data = await res.json();
      setStatus(data);
      if (lastUpdatedTimeRef.current) {
        const ts = data.last_updated;
        const pretty = ts ? new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : new Date().toLocaleString();
        lastUpdatedTimeRef.current.textContent = `Last updated: ${pretty}`;
      }
      if (responseInputRef.current) responseInputRef.current.value = JSON.stringify(data, null, 2);
    } catch (err) {
      if (lastUpdatedTimeRef.current) lastUpdatedTimeRef.current.textContent = `Last updated: ${new Date().toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`;
      if (responseInputRef.current) responseInputRef.current.value = String(err);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setGeoInfoOpen(false);
    }
    if (geoInfoOpen) {
      document.addEventListener("keydown", onKey);
      setTimeout(() => { geoInfoCloseRef.current?.focus(); }, 0);
    }
    return () => document.removeEventListener("keydown", onKey);
  }, [geoInfoOpen]);

  const sendVote = async (vote: "green" | "yellow" | "red" | "unknown") => {
    setLoading(true);
    try {
      // Build payload and optionally attach browser geolocation when consented
      const payload: any = { category: "humanity", vote };
      if (geoConsent && "geolocation" in navigator) {
        // attempt to retrieve browser geolocation (one-shot)
        try {
          const pos: GeolocationPosition = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { maximumAge: 60_000, timeout: 5000 })
          );
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          // minimal geohash encode for precision 4
          const encodeGeohash = (lat: number, lon: number, precision = 4) => {
            const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
            let idx = 0, bit = 0, even = true, geohash = "";
            let latMin = -90, latMax = 90, lonMin = -180, lonMax = 180;
            while (geohash.length < precision) {
              if (even) {
                const mid = (lonMin + lonMax) / 2;
                if (lon >= mid) { idx = (idx << 1) + 1; lonMin = mid; } else { idx = idx << 1; lonMax = mid; }
              } else {
                const mid = (latMin + latMax) / 2;
                if (lat >= mid) { idx = (idx << 1) + 1; latMin = mid; } else { idx = idx << 1; latMax = mid; }
              }
              even = !even; bit++;
              if (bit === 5) { geohash += BASE32[idx]; bit = 0; idx = 0; }
            }
            return geohash;
          };
          const gh = encodeGeohash(lat, lon, 4);
          payload.geo = { method: "browser_geo", geohash: gh };
        //   payload.geo.country = String(lat); // placeholder; server will resolve country/region from geohash
        //   payload.geo.region = String(lon);
          setLastSentGeo(gh);
        } catch (e) {
          // if geo fails, don't block vote; proceed without geo
        }
      }

      const res = await fetch("/api/vote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        await fetchStatus();
        setRateLimitUntil(null);
      } else {
        // show error body / headers (e.g., Retry-After)
        let bodyText: string;
        try {
          bodyText = JSON.stringify(await res.json(), null, 2);
        } catch (e) {
          bodyText = await res.text();
        }
        const retry = res.headers.get("Retry-After");
        // If rate limited, compute retry time and show the alert
          if (res.status === 429) {
          let until: number | null = null;
          if (retry) {
            const asInt = parseInt(retry, 10);
            if (!Number.isNaN(asInt)) {
              until = Date.now() + asInt * 1000;
            } else {
              const parsed = Date.parse(retry);
              if (!Number.isNaN(parsed)) until = parsed;
            }
          }
          if (!until) until = Date.now() + 60_000;
          setRateLimitUntil(until);
        }
        if (responseInputRef.current)
          responseInputRef.current.value = `vote failed: ${res.status} ${res.statusText}\nRetry-After: ${retry || "-"}\n\n${bodyText}`;
      }
    } catch (err) {
      if (responseInputRef.current) responseInputRef.current.value = String(err);
    } finally {
      setLoading(false);
    }
  };

  const statusToEmoji = (s?: string | null) => {
    if (!s) return "loading...";
    switch (s) {
        case "green":
          return <SmileIcon className="h-20 w-20 text-green-500" />;
        case "yellow":
          return <MehIcon className="h-20 w-20 text-yellow-400" />;
        case "red":
          return <FrownIcon className="h-20 w-20 text-red-500" />;
        case "unknown":
          return <HelpIcon className="h-20 w-20 text-muted-foreground" />;
      default:
        return s;
    }
  };

  function RateLimitCountdown({ until, onExpire }: { until: number; onExpire?: () => void }) {
    const [now, setNow] = useState<number>(Date.now());
    useEffect(() => {
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    }, []);
    const remaining = Math.ceil((until - now) / 1000);
    useEffect(() => {
      if (remaining <= 0 && onExpire) onExpire();
    }, [remaining, onExpire]);
    if (remaining <= 0) return <span>now</span>;
    return <span>{remaining}s</span>;
  }

  const isCurrentlyRateLimited = rateLimitUntil ? rateLimitUntil > Date.now() : false;

  // no-op: alert is shown whenever `rateLimitUntil` is set

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-2">
          <Label className="text-lg">Current status</Label>
          <div className="text-5xl leading-none">{status ? statusToEmoji(status.status) : "…"}</div>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mt-2">
          <Button variant="outline" size="lg"  onClick={() => sendVote("green")} disabled={loading || isCurrentlyRateLimited}>
            <SmileIcon className="h-5 w-5 text-foreground/70" /> Doing okay
          </Button>
          <Button variant="outline" size="lg" onClick={() => sendVote("yellow")} disabled={loading || isCurrentlyRateLimited}>
            <MehIcon className="h-5 w-5 text-foreground/70" /> Struggling a bit
          </Button>
          <Button variant="outline" size="lg" onClick={() => sendVote("red")} disabled={loading || isCurrentlyRateLimited}>
            <FrownIcon className="h-5 w-5 text-foreground/70" /> Not doing well
          </Button>
        </div>
        <div className="mt-2 text-sm">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={geoConsent} onChange={(e) => setGeoConsent(e.target.checked)} />
              <span>Share coarse location (one-time, geohash_4) — opt-in</span>
            </label>
            <button
              aria-expanded={geoInfoOpen}
              aria-controls="geohash-info-vote"
              aria-label="About geohash_4"
              onClick={() => setGeoInfoOpen((s) => !s)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setGeoInfoOpen((s) => !s); } }}
              style={{
                background: "#eef2f8",
                border: "1px solid #d7e7fb",
                borderRadius: 9999,
                width: 26,
                height: 26,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              i
            </button>
          </div>
          {lastSentGeo ? <div className="mt-1 text-xs text-muted-foreground">Last sent geohash: {lastSentGeo}</div> : null}
          {geoInfoOpen && (
            <div id="geohash-info-vote" role="dialog" aria-modal="false" aria-labelledby="geohash-info-vote-title" style={{ marginTop: 8 }}>
              <div style={{ background: "#fff", border: "1px solid #e6eef8", padding: 10, borderRadius: 6, maxWidth: 720 }}>
                <h4 id="geohash-info-vote-title" style={{ margin: "0 0 6px 0", fontSize: 13 }}>About geohash_4</h4>
                <p style={{ margin: 0, fontSize: 13, color: "#111" }}>
                  This groups locations using <strong>geohash_4</strong>, a coarse spatial grid. Each value is an
                  aggregated count for a grid cell rather than an individual location. Precision 4 is coarse
                  (tens of kilometers) to help protect privacy while still showing general patterns.
                </p>
                <div style={{ marginTop: 8 }}>
                  <button ref={geoInfoCloseRef} onClick={() => setGeoInfoOpen(false)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #cfe0f8", background: "#f1f9ff", cursor: "pointer" }}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
        {rateLimitUntil ? (
          <div className="mt-4 w-full max-w-md">
            <div className="flex items-start gap-3 rounded-md border bg-red-50/60 p-3 text-sm text-red-800 shadow-sm dark:bg-red-900/30 dark:text-red-200">
              <div className="rounded bg-red-100/60 p-2 text-red-700 dark:bg-red-800/40">
                <Clock className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Rate limited</div>
                <div className="mt-1 text-sm text-red-700/90 dark:text-red-200/90">
                  Your vote didn't count. Try again in <strong><RateLimitCountdown until={rateLimitUntil} onExpire={() => setRateLimitUntil(null)} /></strong>.
                </div>
              </div>
              {/* Dismiss removed: alert must remain until the rate-limit expires */}
            </div>
          </div>
        ) : null}
      </div>

      {/* <Label htmlFor="response" className="sr-only">
        Response
      </Label>
      <Textarea ref={responseInputRef} id="response" readOnly placeholder="Response will appear here..." className="min-h-[140px] font-mono resize-y" /> */}
    </div>
  );
}
